import {doc, setDoc, getDoc, deleteField, Timestamp} from 'firebase/firestore';
import {db} from '../firebase';
import {request} from './apiClient';

/**
 * Note edit tracker — mirrors iOS NoteEditTracker.
 *
 * Captures physician edits to AI-generated notes, debounces trivial changes,
 * maintains a rolling window of edit records, and periodically summarizes
 * patterns via LLM. The summary + recent examples are injected into future
 * note generation prompts to continuously improve output quality.
 *
 * Local layer:  localStorage (fast prompt injection reads)
 * Remote layer: Firestore User/{userId}.noteEditTracking (durability + cross-device)
 *
 * Storage budget: ~20KB per user (30 records × ~600 bytes + summary)
 */

const LS_SUMMARY_KEY = 'scribe_edit_summary';
const LS_EXAMPLES_KEY = 'scribe_edit_examples';
const LS_RECORDS_KEY = 'scribe_edit_records';
const LS_TOTAL_KEY = 'scribe_edit_total_count';
const LS_SINCE_SUMMARY_KEY = 'scribe_edits_since_summary';

const MAX_RECORDS = 30;
const SUMMARIZE_THRESHOLD = 10;
const SNIPPET_MAX = 300;
const MIN_CHAR_DELTA = 20;
const MIN_PERCENT_DELTA = 0.1;
const MIN_WORD_CHANGES = 3;
const MAX_PROMPT_EXAMPLES = 3;
const PROMPT_SNIPPET_LENGTH = 150;

interface EditRecord {
  id: string;
  timestamp: string;
  templateName: string;
  sectionName: string;
  originalSnippet: string;
  editedSnippet: string;
  originalLength: number;
  editedLength: number;
}

class NoteEditTrackerService {
  summary: string;
  records: EditRecord[];
  totalEditCount: number;
  editsSinceLastSummary: number;
  _isSummarizing: boolean;

  constructor() {
    this.summary = '';
    this.records = [];
    this.totalEditCount = 0;
    this.editsSinceLastSummary = 0;
    this._isSummarizing = false;
    this._loadFromCache();
  }

  /**
   * Compare original AI sections with physician edits, store significant diffs.
   * Call once on note save / dismiss — not on every keystroke.
   */
  captureEdits(
    originalSections: Record<string, string>,
    editedSections: Record<string, string>,
    templateName: string,
    userId?: string | null,
  ) {
    const newRecords: EditRecord[] = [];

    for (const [sectionName, editedText] of Object.entries(editedSections)) {
      const originalText = originalSections[sectionName];
      if (!originalText) continue;
      if (!this._isSignificantEdit(originalText, editedText)) continue;

      newRecords.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        timestamp: new Date().toISOString(),
        templateName,
        sectionName,
        originalSnippet: originalText.slice(0, SNIPPET_MAX),
        editedSnippet: editedText.slice(0, SNIPPET_MAX),
        originalLength: originalText.length,
        editedLength: editedText.length,
      });
    }

    if (newRecords.length === 0) return;

    this.records.push(...newRecords);
    this.totalEditCount += newRecords.length;
    this.editsSinceLastSummary += newRecords.length;

    if (this.records.length > MAX_RECORDS) {
      this.records = this.records.slice(-MAX_RECORDS);
    }

    this._saveToCache();
    if (userId) this.syncToFirebase(userId);

    if (
      this.editsSinceLastSummary >= SUMMARIZE_THRESHOLD &&
      !this._isSummarizing
    ) {
      this._generateSummary(userId);
    }
  }

  // ── Significance filter ──────────────────────────────────────

  _isSignificantEdit(original: string, edited: string): boolean {
    const a = original.trim();
    const b = edited.trim();
    if (a === b) return false;

    const charDelta = Math.abs(a.length - b.length);
    const maxLen = Math.max(a.length, b.length, 1);
    const percentDelta = charDelta / maxLen;

    if (charDelta >= MIN_CHAR_DELTA || percentDelta >= MIN_PERCENT_DELTA)
      return true;

    const wordsA = new Set(a.split(/\s+/).filter(Boolean));
    const wordsB = new Set(b.split(/\s+/).filter(Boolean));
    const added = [...wordsB].filter(w => !wordsA.has(w)).length;
    const removed = [...wordsA].filter(w => !wordsB.has(w)).length;
    return added + removed >= MIN_WORD_CHANGES;
  }

  // ── Summary generation ───────────────────────────────────────

  async _generateSummary(userId?: string | null) {
    if (this.records.length === 0) return;
    this._isSummarizing = true;

    try {
      const recent = this.records.slice(-15);
      const editExamples = recent
        .map(
          (r, i) =>
            `Edit ${i + 1} — ${r.sectionName} (${r.templateName}):\n  AI wrote: "${r.originalSnippet}"\n  Doctor changed to: "${r.editedSnippet}"`,
        )
        .join('\n\n');

      const existingCtx = this.summary
        ? `\nPrevious summary (update if new patterns emerge):\n${this.summary}\n\n`
        : '';

      const prompt = `Analyze the following examples of a physician editing AI-generated clinical notes. Identify consistent patterns.${existingCtx}\nRecent edits (${recent.length} examples):\n${editExamples}\n\nSummarize this physician's documentation preferences in 3-5 concise, actionable sentences. Focus on formatting choices, abbreviation usage, level of detail, terminology preferences, structural patterns, and content they consistently add or remove. No preamble — just the summary.`;

      const response = await request('/api/v1/chat/completions', {
        method: 'POST',
        parse: 'raw',
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: 'You are a medical documentation analyst.',
            },
            {role: 'user', content: prompt},
          ],
          temperature: 0.3,
          max_tokens: 500,
        }),
      });

      if (response.ok) {
        const payload = await response.json();
        const newSummary = (
          payload?.choices?.[0]?.message?.content || ''
        ).trim();
        if (newSummary) {
          this.summary = newSummary;
          this.editsSinceLastSummary = 0;
          this._saveToCache();
          if (userId) this.syncToFirebase(userId);
        }
      }
    } catch (err) {
      console.error('NoteEditTracker: Summary generation failed:', err);
    } finally {
      this._isSummarizing = false;
    }
  }

  // ── Prompt context (injected into note generation) ───────────

  getPromptContext(): string {
    const parts: string[] = [];
    if (this.summary) {
      parts.push(`Physician's documentation preferences:\n${this.summary}`);
    }

    const examples = this.records.slice(-MAX_PROMPT_EXAMPLES);
    if (examples.length > 0) {
      const exStr = examples
        .map(r => {
          const orig = r.originalSnippet.slice(0, PROMPT_SNIPPET_LENGTH);
          const edit = r.editedSnippet.slice(0, PROMPT_SNIPPET_LENGTH);
          return `- [${r.sectionName}] AI wrote: "${orig}" → Doctor changed to: "${edit}"`;
        })
        .join('\n');
      parts.push(`Recent edit examples:\n${exStr}`);
    }

    return parts.join('\n\n');
  }

  // ── Local cache ──────────────────────────────────────────────

  _loadFromCache() {
    try {
      this.summary = localStorage.getItem(LS_SUMMARY_KEY) || '';
      this.totalEditCount = parseInt(
        localStorage.getItem(LS_TOTAL_KEY) || '0',
        10,
      );
      this.editsSinceLastSummary = parseInt(
        localStorage.getItem(LS_SINCE_SUMMARY_KEY) || '0',
        10,
      );
      const stored = localStorage.getItem(LS_RECORDS_KEY);
      this.records = stored ? JSON.parse(stored) : [];
    } catch {
      this.records = [];
    }
  }

  _saveToCache() {
    localStorage.setItem(LS_SUMMARY_KEY, this.summary);
    localStorage.setItem(LS_TOTAL_KEY, String(this.totalEditCount));
    localStorage.setItem(
      LS_SINCE_SUMMARY_KEY,
      String(this.editsSinceLastSummary),
    );
    localStorage.setItem(LS_RECORDS_KEY, JSON.stringify(this.records));
    localStorage.setItem(LS_EXAMPLES_KEY, this._buildExamplesForPrompt());
  }

  _buildExamplesForPrompt() {
    const recent = this.records.slice(-MAX_PROMPT_EXAMPLES);
    if (recent.length === 0) return '';
    return recent
      .map(r => {
        const orig = r.originalSnippet.slice(0, PROMPT_SNIPPET_LENGTH);
        const edit = r.editedSnippet.slice(0, PROMPT_SNIPPET_LENGTH);
        return `- [${r.sectionName}] AI wrote: "${orig}" → Doctor changed to: "${edit}"`;
      })
      .join('\n');
  }

  // ── Firebase sync ────────────────────────────────────────────

  async syncToFirebase(userId: string) {
    if (!userId) return;
    const data = {
      summary: this.summary,
      totalEditCount: this.totalEditCount,
      editsSinceLastSummary: this.editsSinceLastSummary,
      records: this.records,
      lastUpdated: Timestamp.now(),
    };

    try {
      await setDoc(
        doc(db, 'User', userId),
        {noteEditTracking: data},
        {merge: true},
      );
    } catch (err) {
      console.error('NoteEditTracker: Firestore write failed:', err);
    }
  }

  async syncFromFirebase(userId: string) {
    if (!userId) return;
    try {
      const snap = await getDoc(doc(db, 'User', userId));
      if (!snap.exists()) {
        this.clearLocalCache();
        return;
      }

      const tracking = snap.data()?.noteEditTracking;
      if (!tracking) {
        this.clearLocalCache();
        return;
      }

      this.summary = tracking.summary || '';
      this.totalEditCount = tracking.totalEditCount || 0;
      this.editsSinceLastSummary = tracking.editsSinceLastSummary || 0;
      this.records = Array.isArray(tracking.records) ? tracking.records : [];
      this._saveToCache();
    } catch (err) {
      console.error('NoteEditTracker: Firestore read failed:', err);
    }
  }

  clearLocalCache() {
    this.summary = '';
    this.records = [];
    this.totalEditCount = 0;
    this.editsSinceLastSummary = 0;
    [
      LS_SUMMARY_KEY,
      LS_EXAMPLES_KEY,
      LS_RECORDS_KEY,
      LS_TOTAL_KEY,
      LS_SINCE_SUMMARY_KEY,
    ].forEach(k => localStorage.removeItem(k));
  }

  resetAll(userId?: string | null) {
    this.clearLocalCache();
    if (userId) {
      setDoc(
        doc(db, 'User', userId),
        {noteEditTracking: deleteField()},
        {merge: true},
      ).catch(err =>
        console.error('NoteEditTracker: Firestore reset failed:', err),
      );
    }
  }
}

export const noteEditTracker = new NoteEditTrackerService();
